import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LayoutGrid, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const result = await register(email, password, name, phone);
    setLoading(false);

    if (result.success) {
      toast.success('Account created successfully!');
      navigate('/configurator');
    } else {
      toast.error(result.error || 'Registration failed');
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1527781277828-b91cf323503a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3VzZSUyMHdpbmRvdyUyMGludGVyaW9yfGVufDB8fHx8MTc3NjQ0MTM2M3ww&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/40"></div>
        <div className="relative z-10 p-12 flex flex-col justify-end text-white">
          <h1 className="text-5xl font-medium tracking-tight mb-4">Start Your Project Today</h1>
          <p className="text-xl text-slate-100">Join thousands of satisfied customers</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <LayoutGrid size={40} className="text-[#0052FF]" />
              <h2 className="text-3xl font-medium tracking-tight text-slate-900">Hi Tech Industries</h2>
            </div>
            <div>
              <h3 className="text-2xl font-medium text-slate-900">Create your account</h3>
              <p className="text-sm text-slate-500 mt-2">Get started with custom window designs</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">
                Full Name
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="John Doe"
                className="h-11 rounded-sm border-slate-200"
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="john@example.com"
                className="h-11 rounded-sm border-slate-200"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">
                Phone Number
              </Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="h-11 rounded-sm border-slate-200"
                data-testid="register-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="h-11 rounded-sm border-slate-200"
                data-testid="register-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0052FF] hover:bg-[#0043D1] text-white rounded-sm transition-all duration-150"
              data-testid="register-submit-button"
            >
              {loading ? (
                'Creating account...'
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <UserPlus size={20} />
                  Create Account
                </span>
              )}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#0052FF] hover:text-[#0043D1] font-medium" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
